import {
  TApiParams,
  TApiResponse,
  TApiProject,
  TApiUsersAccount,
  TApiResponseMeta,
} from "./types"
import BaseApi, { getQueryString } from "./base"
import moment from "moment-timezone"
import _omit from "lodash/omit"
import { cloneDeep } from "lodash"

export type TApiListProject = TApiProject & {
  totalTime: number
  usersAccounts: null | Array<TApiUsersAccount>
  usersAccountTotalCount: null | number
}

export type TApiProjectsListFilters = {
  clientHashIds?: Array<string>
  membersGroupHashIds?: Array<string>
  state?: string // active/archived
  usersAccountHashIds?: Array<string>
  isBillable?: boolean
  name?: string
}

export type TApiProjectsSorting = {
  name?: "desc" | "asc"
}

export interface IGetProjectsListParams extends TApiParams {
  sorting?: TApiProjectsSorting
  filters?: TApiProjectsListFilters
}

export interface IGetProjectsListResponse extends TApiResponse {
  projects: Array<TApiListProject>
  meta: TApiResponseMeta
}

export interface ICreateProjectParams extends TApiParams {
  name: string
  clientId: null | string
  visibility?: boolean
  color: string
  originIdentifier?: string
  origin?: string
  pinned?: boolean
  billableEnabled: boolean
  billableType: "equal" | "custom"
  billableRate: number
  budgetEnabled?: boolean
  budgetType?: "money" | "time"
  budgetMoney: number
  budgetTime: number
  budgetAlertThreshold?: number // integer
  taskCreator?: "nobody" | "member" | "project_manager" | "admin" | string
  taskCreationPolicy?:
    | "internal"
    | "external_mandatory"
    | "external_optional"
    | string
  disallowOverbudget?: boolean
  disallowUserOverbudget?: boolean
  archivedAt: null | string
  defaultIsBillableTimeLogs?: boolean
  additionalDetails?: object
}

export interface ICreateProjectResponse extends TApiResponse {
  project: TApiProject
}

export interface IUpdateProjectResponse extends ICreateProjectResponse {}

type TTimeDataRecord = {
  duration: number
  costAmount: string
  billableAmount: string
}

type TProjectInsightsTotals = {
  billableAmount: string
  costAmount: string
  duration: number
  billableDuration: number
}

type TTimeDataMap = {
  [index: number]: TTimeDataRecord
}

export type TProjectInsightsUsersData = {
  billableAmount: null | string
  budgetTime: null | string
  costAmount: null | string
  duration: number
  usersAccount: TApiUsersAccount
}

export interface IGetProjectInsightsResponse extends TApiResponse {
  timeData: TTimeDataMap
  totals: TProjectInsightsTotals
  usersData: TProjectInsightsUsersData[]
}

function removeUndefinedKeys<T extends Record<string, any>>(
  obj: T
): Partial<T> {
  const newObj: Partial<T> = {}
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined) {
      newObj[key] = obj[key]
    }
  })

  return newObj
}

class ProjectsApi extends BaseApi {
  async getProjectsList(
    params: IGetProjectsListParams
  ): Promise<IGetProjectsListResponse> {
    let queryParams = {
      q: { s: this.prepareSortingParams(params.sorting) },
      ..._omit(params, ["sorting"]),
    }

    const queryWithFiltersCleaned = {
      ...queryParams,
      filters: removeUndefinedKeys(queryParams.filters || {}),
    }

    const query = getQueryString(
      this.paramsToSnakeCase(queryWithFiltersCleaned),
      true
    )

    let response = this.get(`/projects/list${query}`)
    return await this.getResponseJson(response)
  }

  async getProjectSections(projectId: string, filters) {

    const reqFilters = cloneDeep(filters)

    if (reqFilters.users !== undefined && reqFilters.users.length > 0) {
      const userStrings = reqFilters.users.map((userHashId) => {
        if (userHashId == 'no_assignee') return 'no_assignee'
        return `${userHashId}|UsersAccount`
      })
      reqFilters.assignees = userStrings
      delete reqFilters.users
    }

    if (reqFilters.groups !== undefined && reqFilters.groups.length > 0) {
      const groupSrings = reqFilters.groups.map((groupHashId) => {
        return `${groupHashId}|MembersGroup`
      })

      if (!reqFilters.assignees) {
        reqFilters.assignees = []
      }

      reqFilters.assignees = [...reqFilters.assignees, ...groupSrings]
      delete reqFilters.groups
    }

    // Mirror tasks state to fetch sections and remove section specific filter from tasks filter base
    let sectionFilters: any = undefined
    if (reqFilters.sectionStatus !== undefined) {
      sectionFilters = {
        filters: {
          status: reqFilters.sectionStatus
        }
      }
      delete reqFilters.sectionStatus
    }

    const query = filters ? getQueryString(
      this.paramsToSnakeCase({
        ...sectionFilters,
        // TODO: pass limit from outside
        tasks: { 
          filters: reqFilters,
          limit: 10
        }
      }),
      true
    ) : ""

    return this.get(`/projects/${projectId}/project_sections${query}`)
  }

  async getProjectInsights(
    projectId: string
  ): Promise<IGetProjectInsightsResponse> {
    let response = this.get(`/projects/${projectId}/insights`)

    // TRANSLATE BUDGET TIME FROM MINUTES TO HOURS FORMAT
    const json = await this.getResponseJson(response)

    if (json.usersData) {
      json.usersData = json.usersData.map((puser) => {
        if (puser.budgetTime !== null && puser.budgetTime !== undefined) {
          puser.budgetTime = puser.budgetTime / 60
        }

        return puser
      })
    }

    return json
  }

  async getProject(projectId: string) {
    let response = this.get(`/projects/${projectId}`)
    return await this.getResponseJson(response)
  }

  async getProjects(params?: TApiParams): Promise<IGetProjectsListResponse> {
    const query = getQueryString(this.paramsToSnakeCase(params), true)

    let response = this.get(`/projects${query}`)
    return await this.getResponseJson(response)
  }

  async createProject(
    params: ICreateProjectParams
  ): Promise<ICreateProjectResponse> {
    const requestParams = _omit(params, ["pinned"]) // TODO: API returns 500 with pinned inside

    return this.post("/projects", {
      project: requestParams,
    })
  }

  async synchroniseProject(projectId: string) {
    let response = this.patch(`/projects/${projectId}/synchronise`, {})
    return await this.getResponseJson(response)
  }

  async disconnectProject(projectId: string) {
    let response = this.patch(`/projects/${projectId}/disconnect`, {})
    return await this.getResponseJson(response)
  }

  async reconnectProject(projectId: string) {
    let response = this.patch(`/projects/${projectId}/reconnect`, {})
    return await this.getResponseJson(response)
  }

  async deleteProject(projectId: string) {
    let response = this.delete(`/projects/${projectId}`)
    return await this.getResponseJson(response)
  }

  async updateProject(
    projectId: string,
    params: Partial<ICreateProjectParams>
  ): Promise<IUpdateProjectResponse> {
    let response = this.patch(
      `/projects/${projectId}`,
      this.paramsToSnakeCase({ project: params })
    )
    return await this.getResponseJson(response)
  }

  async pinProject(projectId: string) {
    return this.patch(`/projects/${projectId}/pin`, { project: { pinned: true } })
  }

  async unpinProject(projectId: string) {
    return this.patch(`/projects/${projectId}/pin`, { project: { pinned: false } })
  }

  async archiveProject(projectId: string) {
    let response = this.patch(`/projects/${projectId}`, {
      project: {
        archived_at: moment().format(),
      },
    })
    return await this.getResponseJson(response)
  }

  async restoreProject(projectId: string) {
    let response = this.patch(`/projects/${projectId}/restore`, {})
    return await this.getResponseJson(response)
  }
}

export default ProjectsApi
